import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { Thumbnail } from '../../partials/image/image.types';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { useTheme } from '../../../theming/useTheme';
import { AuthorImage } from './AuthorImage';
import { HorizontalSocialButtonsWrapper, SocialButton } from './AuthorSocial';
import { Article } from '../articleLists/ArticleInterface';
import { AuthorPageFeed } from '../articleLists/AuthorPageFeed';
import { AuthorTopGradient } from './AuthorTopGradient';
import { H1, H4, P, semanticCSS } from '../../../typography/semanticTags';
import { BaseUnit } from '../../../theming/baseUnitDefinition';
import { sectionStyle } from '../pageLayouts/layout.utils';

const componentConfigThemeKey = 'authorPage';

interface AuthorDataProps {
  image: Thumbnail;
  name: string;
  social: { imageLinks: SocialButton[] };
  joinDateText: string;
  joinDate: string;
  articlesNumberText: string;
  totalArticles: number;
  description: string;
  articles: Article[];
  isSecondaryHeaderEnabled: boolean;
}

interface AuthorThemeProps {
  titleColor: string;
  metaDataColor: string;
  color: string;
  baseUnit: BaseUnit;
}

const getStyles = ({ baseUnit, titleColor, metaDataColor, color }: AuthorThemeProps) => StyleSheet.create({
  title: {
    color: titleColor,
    textAlign: 'center',
    maxWidth: '640px',
    margin: `${baseUnit / 2}px auto`,
  },
  metaData: {
    textAlign: 'center',
    color: metaDataColor,
    maxWidth: '640px',
    margin: 'auto',
    marginTop: `${baseUnit}px`,
    marginBottom: `${baseUnit / 2}px`,
  },
  description: {
    color,
    maxWidth: '640px',
    margin: 'auto',
    textAlign: 'center',
  },
});

const getMetaData = ({ joinDateText, joinDate, articlesNumberText, totalArticles }: AuthorDataProps) => {
  const base = `${joinDateText} ${joinDate}`;
  if (totalArticles > 0) {
    return `${base} | ${articlesNumberText}${totalArticles}`;
  }

  return base;
};

export const Author: React.FunctionComponent<AuthorDataProps> = props => {
  const { image, name, social: { imageLinks: socialButtons }, description, articles, isSecondaryHeaderEnabled } = props;
  const theme = useTheme<AuthorThemeProps>(componentConfigThemeKey);
  const styles = getStyles(theme);
  const metaData = getMetaData(props);
  return (
    <ErrorBoundary>
      <section className={css(sectionStyle)}>
        <AuthorTopGradient isSecondaryHeaderEnabled={isSecondaryHeaderEnabled} />
        <AuthorImage image={image} />
        <H1 styles={semanticCSS(styles.title)}>{name}</H1>
        <HorizontalSocialButtonsWrapper buttons={socialButtons} />
        <H4 styles={semanticCSS(styles.metaData)}>{metaData}</H4>
        <P styles={semanticCSS(styles.description)}>{description}</P>
        <AuthorPageFeed articles={articles} />
      </section>
    </ErrorBoundary>
  );
};
